// frontend/admin-portal/src/pages/ProductDetail.jsx
import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  PageHeader,
  Card,
  Button,
  Badge,
  Alert,
  Spinner,
} from "@fiberhome/shared-ui";
import { productService } from "../services/productService";
import { categoryService } from "../services/categoryService";
import { brandService } from "../services/brandService";
import ProductSpecsTable from "../components/products/ProductSpecsTable";
import ProductStockBadge from "../components/products/ProductStockBadge";
import ProductPriceCard from "../components/products/ProductPriceCard";
import ProductStockAdjustModal from "../components/products/ProductStockAdjustModal";
import ProductDeleteModal from "../components/products/ProductDeleteModal";
import ProductModal from "../components/products/ProductModal";
import ProductImagesManager from "../components/products/ProductImagesManager";
import { useSettings } from "../contexts/SettingsContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [product, setProduct] = useState(null);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const { get } = useSettings();
  const lowStockThreshold = get("inventory.lowStockThreshold", 5);
  const [editOpen, setEditOpen] = useState(false);
  const [stockOpen, setStockOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const p = await productService.get(id);
      setProduct(p);
    } catch (err) {
      setError(err.response?.data?.message || err.message || "Not found");
    } finally {
      setLoading(false);
    }
  }, [id]);

  const loadRefs = useCallback(async () => {
    const [cats, brs] = await Promise.all([
      categoryService.list({ isActive: true }),
      brandService.list({ isActive: true }),
    ]);
    setCategories(cats);
    setBrands(brs);
  }, []);

  useEffect(() => {
    load();
    loadRefs();
  }, [load, loadRefs]);

  const toggleActive = async () => {
    try {
      await productService.toggleActive(product.id);
      load();
    } catch (err) {
      alert(err.response?.data?.message || err.message);
    }
  };

  if (loading) {
    return (
      <>
        <PageHeader title="Product" breadcrumbs={["Home", "Products", "…"]} />
        <Card>
          <div className="fh-settings-loading">
            <Spinner size="lg" />
          </div>
        </Card>
      </>
    );
  }

  if (error || !product) {
    return (
      <>
        <PageHeader
          title="Product not found"
          breadcrumbs={["Home", "Products"]}
        />
        <Alert variant="danger">{error || "Product not found"}</Alert>
        <Button
          variant="outline"
          className="mt-3"
          onClick={() => navigate("/products")}
        >
          ← Back to Products
        </Button>
      </>
    );
  }

  return (
    <>
      <PageHeader
        title={product.name}
        subtitle={product.sku}
        breadcrumbs={["Home", "Products", product.sku]}
        actions={
          <div className="d-flex gap-2 flex-wrap">
            <Button variant="outline" size="sm" onClick={toggleActive}>
              {product.isActive ? "Hide" : "Show"}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setStockOpen(true)}
            >
              Adjust Stock
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => setEditOpen(true)}
            >
              Edit
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setDeleteOpen(true)}
            >
              Delete
            </Button>
          </div>
        }
      />

      <div className="row g-3">
        {/* LEFT column — photos + description */}
        <div className="col-lg-5 col-md-6">
          <Card title="Photos">
            <ProductImagesManager product={product} onSaved={load} />
          </Card>

          <div className="mt-3">
            <Card title="Description">
              <div
                style={{
                  whiteSpace: "pre-wrap",
                  fontSize: 13.5,
                  color: "var(--fh-text)",
                }}
              >
                {product.description || (
                  <span className="text-muted">No description.</span>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* RIGHT column — meta, price, specs */}
        <div className="col-lg-7 col-md-6">
          <Card>
            <div className="fh-product-meta">
              <div className="fh-product-meta__row">
                <span className="text-muted">Category</span>
                <span>
                  {product.category ? (
                    <Badge variant="primary">{product.category.name}</Badge>
                  ) : (
                    <span className="text-muted">—</span>
                  )}
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Kind</span>
                <Badge variant="info">{product.category?.kind || "—"}</Badge>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Brand</span>
                <span>
                  {product.brand?.name || <span className="text-muted">—</span>}
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Status</span>
                <Badge variant={product.isActive ? "success" : "default"}>
                  {product.isActive ? "Active" : "Hidden"}
                </Badge>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Stock</span>
                <span className="d-flex align-items-center gap-2">
                  <strong>
                    {product.stock} {product.unit}
                  </strong>
                  <ProductStockBadge product={product} />
                </span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Min Stock</span>
                <span>{product.minStock}</span>
              </div>
              <div className="fh-product-meta__row">
                <span className="text-muted">Barcode</span>
                <span>
                  {product.barcode || <span className="text-muted">—</span>}
                </span>
              </div>
            </div>
          </Card>

          <div className="mt-3">
            <ProductPriceCard product={product} />
          </div>

          <div className="mt-3">
            <ProductSpecsTable
              category={product.category}
              attributes={product.attributes}
            />
          </div>
        </div>
      </div>

      {/* Modals */}
      <ProductModal
        open={editOpen}
        product={product}
        categories={categories}
        brands={brands}
        lowStockThreshold={lowStockThreshold}
        onClose={() => setEditOpen(false)}
        onSaved={load}
      />
      <ProductStockAdjustModal
        open={stockOpen}
        product={product}
        onClose={() => setStockOpen(false)}
        onSaved={load}
      />
      <ProductDeleteModal
        open={deleteOpen}
        product={product}
        onClose={() => setDeleteOpen(false)}
        onDeleted={() => navigate("/products")}
      />
    </>
  );
}
